import { useId, useState } from "react";

import { RemoteSelect } from "@/components/fields/remote-select";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { existingChoiceLabel, pageProblemText } from "@/lib/tina/festival-page";
import { useArtistRepository } from "@/pages/artists/hooks/use-artist-repository";
import type { ArtistNestedType } from "@/pages/artists/schemas/artist.nested.form";
import { VenuePicker } from "@/pages/tina/festivals/components/venue-picker";
import {
  type SuggestionInput,
  acceptBody,
  canAccept,
  initialInput,
} from "@/pages/tina/festivals/lib/suggestions";
import type {
  ExistingChoiceKind,
  SuggestionAcceptBody,
  SuggestionRead,
} from "@/types/tina-festival-page";

interface SuggestionRowProps {
  suggestion: SuggestionRead;
  /** Each day's number by its date, for "Use as Day 2". */
  dayNumbers: Map<string, number>;
  busy: boolean;
  onAccept: (body: SuggestionAcceptBody) => void;
  onDismiss: () => void;
  /** Tina refused the answer (a 422): its problem keys, shown on this suggestion and nowhere else. */
  problems?: string[];
}

const REPLACE = "replace";
const NOT_PART = "not_part";
const USE_PREFIX = "use:";

function ArtistPick({
  label,
  value,
  onChange,
}: {
  label: string;
  value: SuggestionInput["artist"];
  onChange: (artist: SuggestionInput["artist"]) => void;
}) {
  const { search } = useArtistRepository();
  const id = useId();
  return (
    <div className="min-w-0 max-w-sm">
      <span id={`${id}-label`} className="sr-only">
        {label}
      </span>
      <RemoteSelect<ArtistNestedType>
        triggerType="change"
        showSelected
        placeholder="Search Mood artists"
        triggerId={`${id}-trigger`}
        ariaLabelledBy={`${id}-label ${id}-trigger`}
        className="w-full font-normal"
        value={
          value === null
            ? undefined
            : ({ id: value.id, name: value.name } as ArtistNestedType)
        }
        onValueChanged={(selected) =>
          onChange(
            selected === undefined || !selected.id
              ? null
              : {
                  id: selected.id,
                  name: selected.name,
                  photoUrl:
                    selected.photo?.url ??
                    selected.media?.photos?.[0]?.url ??
                    null,
                },
          )
        }
        accessorFn={(option) => ({
          id: option?.id || option?.name,
          name: option ? `${option.name}` : "",
          iconUrl: option?.media?.photos?.[0]?.url,
        })}
        onSearch={async (query, onStatusChanged) => {
          onStatusChanged("loading");
          const result = await search(query);
          onStatusChanged("success");
          return result;
        }}
      />
    </div>
  );
}

/** One open suggestion of an approved page: Tina's sentence as it is, the answers it needs, Accept and Dismiss. */
export function SuggestionRow({
  suggestion,
  dayNumbers,
  busy,
  onAccept,
  onDismiss,
  problems = [],
}: SuggestionRowProps) {
  const [input, setInput] = useState(() => initialInput(suggestion));
  const subject = suggestion.name ?? "this suggestion";
  const dates = [...dayNumbers.entries()].sort(([a], [b]) => (a < b ? -1 : 1));

  const choiceValue =
    input.choice === "use_as_day" && input.day !== null
      ? `${USE_PREFIX}${input.day}`
      : (input.choice ?? undefined);
  const setChoice = (value: string) => {
    if (value.startsWith(USE_PREFIX)) {
      setInput({
        ...input,
        choice: "use_as_day",
        day: value.slice(USE_PREFIX.length),
      });
    } else {
      setInput({
        ...input,
        choice: value as ExistingChoiceKind,
        day: input.day ?? dates[0]?.[0] ?? null,
      });
    }
  };

  return (
    <div
      role="group"
      aria-label={suggestion.text}
      className="flex flex-col gap-2 rounded-md border border-dashed border-amber-400 p-3"
    >
      <p className="text-sm">{suggestion.text}</p>
      {problems.length > 0 ? (
        <ul className="list-disc pl-5 text-sm text-red-700">
          {problems.map((problem) => (
            <li key={problem}>{pageProblemText(problem)}</li>
          ))}
        </ul>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        {suggestion.needs.includes("venue") ? (
          <div className="w-72">
            <VenuePicker
              label="Venue for the new day"
              value={input.venue ?? undefined}
              onChange={(venue) =>
                setInput({
                  ...input,
                  venue: venue ? { id: venue.id, name: venue.name } : null,
                })
              }
            />
          </div>
        ) : null}
        {suggestion.needs.includes("artist") ? (
          <ArtistPick
            label={`Artist for ${subject}`}
            value={input.artist}
            onChange={(artist) => setInput({ ...input, artist })}
          />
        ) : null}
        {suggestion.needs.includes("choice") ? (
          <Select value={choiceValue} onValueChange={setChoice}>
            <SelectTrigger
              className="w-64"
              aria-label={`Answer for ${subject}`}
            >
              <SelectValue placeholder="Choose" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={REPLACE}>
                {existingChoiceLabel("replace", null)}
              </SelectItem>
              {dates.map(([date, number]) => (
                <SelectItem key={date} value={`${USE_PREFIX}${date}`}>
                  {existingChoiceLabel("use_as_day", number)}
                </SelectItem>
              ))}
              <SelectItem value={NOT_PART}>
                {existingChoiceLabel("not_part", null)}
              </SelectItem>
            </SelectContent>
          </Select>
        ) : null}
        {suggestion.kind === "new_name" ? (
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={input.makeCanonical}
              onCheckedChange={(checked) =>
                setInput({ ...input, makeCanonical: checked === true })
              }
            />
            Make it the main name
          </label>
        ) : null}
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          disabled={busy || !canAccept(suggestion, input)}
          onClick={() => onAccept(acceptBody(suggestion, input))}
        >
          Accept
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={onDismiss}
        >
          Dismiss
        </Button>
      </div>
    </div>
  );
}
